import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';

const AdminTimetableManager = () => {
  const [activeSection, setActiveSection] = useState('templates');
  const [globalTemplate, setGlobalTemplate] = useState({
    startTime: { hours: 7, minutes: 10 },
    endTime: { hours: 13, minutes: 45 },
    lessonDuration: 50,
    breakDuration: 20,
    includeBreaks: true,
    periods: []
  });
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [newRoom, setNewRoom] = useState('');
  const [editingPeriod, setEditingPeriod] = useState(null);

  const server = buildApiUrl("");

  // Load data on component mount
  useEffect(() => {
    loadGlobalData();
  }, []);

  const loadGlobalData = async () => {
    try {
      // Load global template
      const templateRes = await fetch(`${server}/admin-global-template`);
      if (templateRes.ok) {
        const templateData = await templateRes.json();
        setGlobalTemplate(templateData);
      }

      // Load subjects
      const subjectsRes = await fetch(`${server}/admin-subjects`);
      if (subjectsRes.ok) {
        const subjectsData = await subjectsRes.json();
        setSubjects(subjectsData);
      }

      // Load rooms
      const roomsRes = await fetch(`${server}/admin-rooms`);
      if (roomsRes.ok) {
        const roomsData = await roomsRes.json();
        setRooms(roomsData);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  const saveGlobalTemplate = async () => {
    try {
      const response = await fetch(`${server}/admin-global-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(globalTemplate)
      });
      
      if (response.ok) {
        alert('Global template saved successfully!');
      } else {
        alert('Failed to save global template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    }
  };

  const addSubject = async () => {
    if (!newSubject.trim()) return;
    
    try {
      const response = await fetch(`${server}/admin-subjects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          subject: newSubject.trim(),
          color: '#3B82F6' // Default blue color
        })
      });
      
      if (response.ok) {
        const newSubjectData = await response.json();
        setSubjects(prev => [...prev, newSubjectData]);
        setNewSubject('');
      }
    } catch (error) {
      console.error('Error adding subject:', error);
    }
  };

  const removeSubject = async (subjectId) => {
    try {
      const response = await fetch(`${server}/admin-subjects/${subjectId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setSubjects(prev => prev.filter(s => s.id !== subjectId));
      }
    } catch (error) {
      console.error('Error removing subject:', error);
    }
  };

  const updateSubjectColor = async (subjectId, color) => {
    try {
      const response = await fetch(`${server}/admin-subjects/${subjectId}/color`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ color })
      });
      
      if (response.ok) {
        setSubjects(prev => prev.map(s => 
          s.id === subjectId ? { ...s, color } : s
        ));
      }
    } catch (error) {
      console.error('Error updating subject color:', error);
    }
  };

  const addRoom = async () => {
    if (!newRoom.trim()) return;
    
    try {
      const response = await fetch(`${server}/admin-rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ room: newRoom.trim() })
      });
      
      if (response.ok) {
        setRooms(prev => [...prev, { id: Date.now(), name: newRoom.trim() }]);
        setNewRoom('');
      }
    } catch (error) {
      console.error('Error adding room:', error);
    }
  };

  const removeRoom = async (roomId) => {
    try {
      const response = await fetch(`${server}/admin-rooms/${roomId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setRooms(prev => prev.filter(r => r.id !== roomId));
      }
    } catch (error) {
      console.error('Error removing room:', error);
    }
  };

  const addNewPeriod = () => {
    const newPeriod = {
      id: Date.now(),
      name: `Period ${globalTemplate.periods.length + 1}`,
      startTime: '08:00',
      endTime: '08:50',
      type: 'lesson',
      color: '#3B82F6'
    };
    setGlobalTemplate(prev => ({
      ...prev,
      periods: [...prev.periods, newPeriod]
    }));
    setEditingPeriod(newPeriod.id);
  };

  // ✅ NEW: Quick add 15-minute period following the previous session
  const addQuick15MinPeriod = () => {
    const existingPeriods = globalTemplate.periods || [];
    const lastPeriod = existingPeriods[existingPeriods.length - 1];
    
    // Default start time based on last period's end time or default
    let startTime = '08:00';
    if (lastPeriod && lastPeriod.endTime) {
      // Start immediately after the last period
      startTime = lastPeriod.endTime;
    }
    
    // Calculate end time (15 minutes later)
    const [hours, minutes] = startTime.split(':').map(Number);
    const startDate = new Date();
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate.getTime() + 15 * 60000); // 15 minutes
    const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
    
    const newPeriod = {
      id: Date.now(),
      name: `Period ${existingPeriods.length + 1}`,
      startTime,
      endTime,
      type: 'lesson',
      color: '#3B82F6'
    };
    
    setGlobalTemplate(prev => ({
      ...prev,
      periods: [...prev.periods, newPeriod]
    }));
    setEditingPeriod(newPeriod.id);
  };

  const updatePeriod = (periodId, field, value) => {
    setGlobalTemplate(prev => ({
      ...prev,
      periods: prev.periods.map(period =>
        period.id === periodId ? { ...period, [field]: value } : period
      )
    }));
  };

  const removePeriod = (periodId) => {
    setGlobalTemplate(prev => ({
      ...prev,
      periods: prev.periods.filter(p => p.id !== periodId)
    }));
    setEditingPeriod(null);
  };

  const updateTemplate = (updates) => {
    setGlobalTemplate(prev => ({ ...prev, ...updates }));
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">📅</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Timetable Manager</h1>
                <p className="text-gray-600">Manage global templates, subjects, and rooms for all teachers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 mb-6">
          <button
            onClick={() => setActiveSection('templates')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'templates'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📅 Global Template
          </button>
          <button
            onClick={() => setActiveSection('subjects')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'subjects'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📚 Subjects
          </button>
          <button
            onClick={() => setActiveSection('rooms')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'rooms'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🏫 Rooms
          </button>
        </div>

        {/* Global Template Section */}
        {activeSection === 'templates' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Global Timetable Template</h2>
                <p className="text-gray-600 text-sm">This template will be used by all teachers when creating timetables</p>
              </div>
              <button
                onClick={saveGlobalTemplate}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                💾 Save Template
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <div className="flex gap-2">
                    <select
                      value={globalTemplate.startTime.hours}
                      onChange={(e) => updateTemplate({
                        startTime: { ...globalTemplate.startTime, hours: parseInt(e.target.value) }
                      })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                      ))}
                    </select>
                    <span className="self-center">:</span>
                    <select
                      value={globalTemplate.startTime.minutes}
                      onChange={(e) => updateTemplate({
                        startTime: { ...globalTemplate.startTime, minutes: parseInt(e.target.value) }
                      })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {Array.from({ length: 12 }, (_, i) => i * 5).map(min => (
                        <option key={min} value={min}>{String(min).padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                  <div className="flex gap-2">
                    <select
                      value={globalTemplate.endTime.hours}
                      onChange={(e) => updateTemplate({
                        endTime: { ...globalTemplate.endTime, hours: parseInt(e.target.value) }
                      })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
                      ))}
                    </select>
                    <span className="self-center">:</span>
                    <select
                      value={globalTemplate.endTime.minutes}
                      onChange={(e) => updateTemplate({
                        endTime: { ...globalTemplate.endTime, minutes: parseInt(e.target.value) }
                      })}
                      className="border border-gray-300 rounded px-2 py-1 text-sm"
                    >
                      {Array.from({ length: 12 }, (_, i) => i * 5).map(min => (
                        <option key={min} value={min}>{String(min).padStart(2, '0')}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Duration (min)</label>
                  <input
                    type="number"
                    min="5"
                    max="120"
                    step="5"
                    value={globalTemplate.lessonDuration}
                    onChange={(e) => updateTemplate({ lessonDuration: parseInt(e.target.value) || 50 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Break Duration (min)</label>
                  <input
                    type="number"
                    min="5"
                    max="60"
                    step="5"
                    value={globalTemplate.breakDuration}
                    onChange={(e) => updateTemplate({ breakDuration: parseInt(e.target.value) || 20 })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    disabled={!globalTemplate.includeBreaks}
                  />
                </div>
              </div>

              {/* Include Breaks */}
              <div>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={globalTemplate.includeBreaks}
                    onChange={(e) => updateTemplate({ includeBreaks: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                  />
                  <span className="text-sm font-medium text-gray-700">Include break periods between lessons</span>
                </label>
              </div>

              {/* Periods Management */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Template Periods</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={addNewPeriod}
                      className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                    >
                      + Add Period (50min)
                    </button>
                    <button
                      onClick={addQuick15MinPeriod}
                      className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700 transition-colors flex items-center gap-1"
                      title="Quickly add a 15-minute period following the previous session"
                    >
                      ⚡ Quick 15min
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {globalTemplate.periods.map((period) => (
                    <div key={period.id} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Period Name</label>
                          <input
                            type="text"
                            value={period.name}
                            onChange={(e) => updatePeriod(period.id, 'name', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
                          <input
                            type="time"
                            value={period.startTime}
                            onChange={(e) => updatePeriod(period.id, 'startTime', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
                          <input
                            type="time"
                            value={period.endTime}
                            onChange={(e) => updatePeriod(period.id, 'endTime', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                          <select
                            value={period.type}
                            onChange={(e) => updatePeriod(period.id, 'type', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          >
                            <option value="lesson">📚 Lesson</option>
                            <option value="break">☕ Break</option>
                            <option value="lunch">🍽️ Lunch</option>
                            <option value="assembly">🏛️ Assembly</option>
                            <option value="study">📖 Study Period</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                          <input
                            type="color"
                            value={period.color}
                            onChange={(e) => updatePeriod(period.id, 'color', e.target.value)}
                            className="w-full h-8 border border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={() => removePeriod(period.id)}
                            className="w-full bg-red-500 text-white px-2 py-1 rounded text-sm hover:bg-red-600 transition-colors"
                          >
                            🗑️ Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {globalTemplate.periods.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <p className="text-lg">📅 No periods created yet</p>
                      <p className="text-sm">Click "Add Period" to create your first period</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subjects Section */}
        {activeSection === 'subjects' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Subject Management</h2>
                <p className="text-gray-600 text-sm">Manage subjects that teachers can assign to timetable periods</p>
              </div>
            </div>

            {/* Add New Subject */}
            <div className="mb-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  placeholder="Enter subject name (e.g., Mathematics, English, Science)"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  onKeyPress={(e) => e.key === 'Enter' && addSubject()}
                />
                <button
                  onClick={addSubject}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  📚 Add Subject
                </button>
              </div>
            </div>

            {/* Subjects List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {subjects.map((subject) => (
                <div key={subject.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-3 flex-1">
                    <input
                      type="color"
                      value={subject.color || '#3B82F6'}
                      onChange={(e) => updateSubjectColor(subject.id, e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer border-2 border-gray-300"
                      title="Choose subject color"
                    />
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: subject.color || '#3B82F6' }}
                      ></span>
                      <span className="font-medium text-gray-900">{subject.name || subject.subject}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => removeSubject(subject.id)}
                    className="text-red-500 hover:text-red-700 p-1 rounded"
                    title="Remove subject"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              {subjects.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <p className="text-lg">📚 No subjects added yet</p>
                  <p className="text-sm">Add subjects that teachers can use in their timetables</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rooms Section */}
        {activeSection === 'rooms' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Room Management</h2>
                <p className="text-gray-600 text-sm">Manage rooms that teachers can assign to timetable periods</p>
              </div>
            </div>

            {/* Add New Room */}
            <div className="mb-6">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={newRoom}
                  onChange={(e) => setNewRoom(e.target.value)}
                  placeholder="Enter room name (e.g., Room A1, Science Lab, Library)"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                  onKeyPress={(e) => e.key === 'Enter' && addRoom()}
                />
                <button
                  onClick={addRoom}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  🏫 Add Room
                </button>
              </div>
            </div>

            {/* Rooms List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {rooms.map((room) => (
                <div key={room.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600">🏫</span>
                    <span className="font-medium text-gray-900">{room.name}</span>
                  </div>
                  <button
                    onClick={() => removeRoom(room.id)}
                    className="text-red-500 hover:text-red-700 p-1 rounded"
                    title="Remove room"
                  >
                    🗑️
                  </button>
                </div>
              ))}
              
              {rooms.length === 0 && (
                <div className="col-span-full text-center py-8 text-gray-500">
                  <p className="text-lg">🏫 No rooms added yet</p>
                  <p className="text-sm">Add rooms that teachers can use in their timetables</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTimetableManager;
